/**
 * A carteira: todas as OS da gráfica, vivas, numa tela.
 *
 * É a primeira tela que o Jeferson abre e a que responde "onde está a OS X".
 * Por isso ela é uma **lista densa** e não uma grade de cartões: com 200 OS por
 * mês, o que importa é caber muita linha na tela e comparar prazos de relance.
 *
 * A ordem padrão é a do PRD (RN-033): atrasadas primeiro, depois prazo. Quem
 * quiser outra ordem clica no cabeçalho.
 */

import { formato } from '@pcpgbu/dominio';
import type { LinhaCarteira } from '@pcpgbu/dados';
import {
  botao,
  cartaoOs,
  destacar,
  h,
  marcasDaOs,
  progresso,
  render,
  semaforo,
  sf,
  situacaoDaOs,
  vazio,
  type Filho,
  cabecalhoDePagina,
} from '@pcpgbu/ui';
import type { Aplicacao } from '../estado.js';

type Filtro = 'abertas' | 'atrasadas' | 'producao' | 'prontas' | 'todas';

const FILTROS: Array<{ id: Filtro; rotulo: string }> = [
  { id: 'abertas', rotulo: 'Abertas' },
  { id: 'atrasadas', rotulo: 'Atrasadas' },
  { id: 'producao', rotulo: 'Em produção' },
  { id: 'prontas', rotulo: 'Prontas' },
  { id: 'todas', rotulo: 'Todas' },
];

type Coluna = 'prazo' | 'numero' | 'cliente' | 'situacao';

let filtroAtual: Filtro = 'abertas';
let ordem: { coluna: Coluna; desc: boolean } = { coluna: 'prazo', desc: false };

export async function telaCarteira(app: Aplicacao, destino: HTMLElement): Promise<Filho> {
  const linhas = await app.repo.carteira();
  const busca = app.busca.trim().toLowerCase();

  const desenhar = (): void => {
    const filtradas = aplicarFiltro(linhas, filtroAtual, busca);
    const ordenadas = aplicarOrdem(filtradas);

    render(destino, [
      h('.vstack.gap-3', [
      cabecalhoDePagina({
        olho: 'Produção',
        titulo: 'Carteira',
        lead: 'Toda OS que entrou e ainda não saiu, com onde o material está e quanto tempo falta.',
      }),

        barraDeFiltros(linhas, filtradas.length, busca, () => desenhar()),
        ordenadas.length === 0
          ? semResultado(busca, filtroAtual)
          : tabela(ordenadas, busca, (numero) => app.roteador.ir(`/os/${numero}`), () => desenhar()),
      ]),
    ]);
  };

  desenhar();
  return null;
}

/* ------------------------------------------------------------------ */

function aplicarFiltro(linhas: LinhaCarteira[], filtro: Filtro, busca: string): LinhaCarteira[] {
  let saida = linhas;

  if (filtro === 'abertas') {
    saida = saida.filter(
      (l) => l.estado.status !== 'concluida' && l.estado.status !== 'cancelada',
    );
  } else if (filtro === 'atrasadas') saida = saida.filter((l) => l.atrasada);
  else if (filtro === 'producao') saida = saida.filter((l) => l.estado.status === 'em_producao');
  else if (filtro === 'prontas') saida = saida.filter((l) => l.estado.status === 'pronta');

  if (busca) {
    saida = saida.filter(
      (l) =>
        String(l.os.numero).includes(busca) ||
        l.os.clienteCurto.toLowerCase().includes(busca) ||
        l.os.produto.toLowerCase().includes(busca) ||
        (l.os.orcamento?.vendedor ?? '').toLowerCase().includes(busca),
    );
  }
  return saida;
}

function aplicarOrdem(linhas: LinhaCarteira[]): LinhaCarteira[] {
  const sinal = ordem.desc ? -1 : 1;
  const chave = (l: LinhaCarteira): number | string => {
    if (ordem.coluna === 'numero') return l.os.numero;
    if (ordem.coluna === 'cliente') return l.os.clienteCurto.toLowerCase();
    if (ordem.coluna === 'situacao') return situacaoDaOs(l);
    return Date.parse(l.os.previsaoEntregaEm);
  };
  return [...linhas].sort((a, b) => {
    // Atrasada sobe sempre, em qualquer ordenação: é o que precisa ser visto.
    if (ordem.coluna === 'prazo' && a.atrasada !== b.atrasada) return a.atrasada ? -1 : 1;
    const ka = chave(a);
    const kb = chave(b);
    if (ka < kb) return -1 * sinal;
    if (ka > kb) return 1 * sinal;
    return a.os.numero - b.os.numero;
  });
}

function barraDeFiltros(
  todas: LinhaCarteira[],
  visiveis: number,
  busca: string,
  redesenhar: () => void,
): HTMLElement {
  const conta = (f: Filtro): number => aplicarFiltro(todas, f, '').length;

  return h('.hstack.gap-3.wrap', [
    h(
      '.mac-segmented',
      FILTROS.map((f) =>
        h(
          `button${f.id === filtroAtual ? '.is-active' : ''}`,
          {
            type: 'button',
            onclick: () => {
              filtroAtual = f.id;
              redesenhar();
            },
          },
          [f.rotulo, conta(f.id) > 0 ? h('span.selo.s-cinza', String(conta(f.id))) : null],
        ),
      ),
    ),
    h('.spacer'),
    busca
      ? h('span.t-subhead.t-secondary', `${visiveis} de ${todas.length} — buscando "${busca}"`)
      : h('span.t-subhead.t-secondary', `${visiveis} ${visiveis === 1 ? 'OS' : 'OS'}`),
  ]);
}

function semResultado(busca: string, filtro: Filtro): HTMLElement {
  if (busca) {
    return vazio(
      'magnifyingglass',
      'Nada encontrado',
      `Nenhuma OS com "${busca}" no número, no cliente, no produto ou no vendedor.`,
    );
  }
  if (filtro === 'atrasadas') {
    return vazio('checkmark-circle', 'Nenhuma OS atrasada', 'Tudo dentro do prazo prometido.');
  }
  return vazio(
    'tray',
    'A carteira está vazia',
    'As OS entram sozinhas quando o agente estiver instalado no computador do sistema da gráfica. Enquanto isso, arraste os PDFs na tela de Importações.',
  );
}

/* ------------------------------------------------------------------ */

function tabela(
  linhas: LinhaCarteira[],
  busca: string,
  aoAbrir: (numero: number) => void,
  redesenhar: () => void,
): HTMLElement {
  const cabecalho = (rotulo: string, coluna: Coluna | null, largura?: string): HTMLElement =>
    h(
      'th',
      {
        style: largura ? { width: largura } : {},
        class: coluna ? 'pointer' : '',
        onclick: coluna
          ? () => {
              ordem =
                ordem.coluna === coluna
                  ? { coluna, desc: !ordem.desc }
                  : { coluna, desc: false };
              redesenhar();
            }
          : undefined,
      },
      [
        rotulo,
        coluna && ordem.coluna === coluna
          ? sf(ordem.desc ? 'chevron-down' : 'chevron-up', 'sf-xs')
          : null,
      ],
    );

  return h('.cartao.flat', { style: { padding: '0', overflow: 'hidden' } }, [
    h('table.tab', [
      h('thead', h('tr', [
        // ⚠️ AS LARGURAS SOMAM MENOS QUE A TABELA de propósito: o que sobra vai
        // para `Produto`, que é a única coluna de texto livre. Com as medidas
        // antigas sobravam 173 px para nomes como "Envelope Aberto (Person) -
        // Raio X - 26x36cm (MOD. 229)": cinco linhas por célula, sete OS por
        // tela. A carteira existe para ser varrida de cima a baixo.
        cabecalho('OS', 'numero', '74px'),
        cabecalho('Cliente', 'cliente', '148px'),
        cabecalho('Produto', null),
        cabecalho('Entrega', 'prazo', '116px'),
        cabecalho('Situação', 'situacao', '116px'),
        cabecalho('Onde está', null, '146px'),
        cabecalho('Etapas', null, '96px'),
        cabecalho('Falta', null, '66px'),
      ])),
      h(
        'tbody',
        linhas.map((l) => linhaDaTabela(l, busca, aoAbrir)),
      ),
    ]),
  ]);
}

function linhaDaTabela(
  l: LinhaCarteira,
  busca: string,
  aoAbrir: (numero: number) => void,
): HTMLElement {
  const situacao = situacaoDaOs(l);
  const atrasoTexto = l.atrasada
    ? formato.distanciaNoTempo(l.os.previsaoEntregaEm)
    : formato.distanciaNoTempo(l.os.previsaoEntregaEm);

  return h(
    'tr.clicavel',
    { onclick: () => aoAbrir(l.os.numero) },
    [
      h('td.pcp-mono', { style: { fontWeight: '600' } }, destacar(String(l.os.numero), busca)),
      h('td', { style: { fontWeight: '500' } }, destacar(l.os.clienteCurto, busca)),
      h('td', [
        h('.pcp-produto', { title: `${formato.numero(l.os.quantidade)} · ${l.os.produto}` }, [
          h('span.t-secondary.pcp-num', `${formato.numero(l.os.quantidade)} · `),
          destacar(l.os.produto, busca),
        ]),
        marcasDaOs(l).length > 0
          ? h('.hstack.gap-1', { style: { marginTop: '3px', flexWrap: 'wrap' } }, marcasDaOs(l))
          : null,
      ]),
      h('td', [
        h('div', formato.dataHoraCurta(l.os.previsaoEntregaEm)),
        h(
          'div.t-subhead',
          { style: { color: l.atrasada ? 'var(--sys-red)' : 'var(--label-3)' } },
          atrasoTexto,
        ),
      ]),
      h('td', semaforo(situacao)),
      h('td.t-callout', [
        l.setorAtual ? h('div', l.setorAtual.nome) : h('div.t-tertiary', '—'),
        l.etapaAtual
          ? h('div.t-subhead.t-secondary.t-truncate', { title: l.etapaAtual.nomeOs }, l.etapaAtual.nomeOs)
          : null,
      ]),
      h('td', [
        h('.hstack.gap-2', [
          progresso(
            Array.from({ length: l.estado.etapasTotal }, (_, i) => ({
              estado: { status: i < l.estado.etapasConcluidas ? ('concluida' as const) : ('fila' as const) },
            })),
          ),
          h('span.t-subhead.t-secondary.pcp-num', `${l.estado.etapasConcluidas}/${l.estado.etapasTotal}`),
        ]),
      ]),
      h('td.num.t-callout', { title: 'Tempo previsto que falta' },
        formato.hhmm(l.estado.tempoPrevistoRestanteSeg)),
    ],
  );
}

/** Versão em cartões — usada na busca da barra de menus e em telas estreitas. */
export function listaEmCartoes(
  linhas: LinhaCarteira[],
  aoAbrir: (numero: number) => void,
): HTMLElement {
  return h(
    '.vstack.gap-2',
    linhas.map((l) => cartaoOs(l, { aoClicar: aoAbrir })),
  );
}

export { botao };
